import { projectsProjectAccessDecisionSchema, } from '@crest/module-projects/contracts/project-access-decision';
import { projectsProjectOrderReferenceSnapshotSchema } from '@crest/module-projects/contracts/project-order-reference';
import { z } from 'zod';
import { procurementOperationCounterpartySchema, procurementOperationIdSchema, procurementOperationMoneyCentsSchema, procurementOperationPageSchema, procurementOperationProgressBasisPointsSchema, procurementOperationReasonSchema, procurementOperationStateCommandSchema, } from './procurement-operation-common.contract.js';
export const procurementSubcontractsCapability = 'procurement.subcontracts.v1';
export const procurementSubcontractStatusSchema = z.enum([
    'planned', 'scheduled', 'active', 'complete', 'on_hold', 'cancelled',
]);
export const procurementSubcontractScheduleStatusSchema = z.enum([
    'unscheduled', 'scheduled', 'on_site', 'complete', 'delayed',
]);
export const procurementSubcontractCostTypeSchema = z.enum([
    'fixed', 'hourly', 'allowance', 'time_and_materials', 'unknown',
]);
const procurementSubcontractDraftBaseSchema = z.strictObject({
    projectId: z.string().uuid(),
    counterparty: procurementOperationCounterpartySchema,
    trade: z.string().trim().min(1).max(160),
    status: procurementSubcontractStatusSchema,
    scheduleStatus: procurementSubcontractScheduleStatusSchema,
    costType: procurementSubcontractCostTypeSchema,
    estimatedCostCents: procurementOperationMoneyCentsSchema.nullable(),
    committedCostCents: procurementOperationMoneyCentsSchema.nullable(),
    actualCostCents: procurementOperationMoneyCentsSchema.nullable(),
    progressBasisPoints: procurementOperationProgressBasisPointsSchema,
    notes: z.string().trim().max(8_000).nullable(),
});
function validateSubcontractState(draft, context) {
    if (draft.status === 'complete' && draft.progressBasisPoints !== 10_000) {
        context.addIssue({ code: 'custom', path: ['progressBasisPoints'], message: 'A completed subcontract must be 100% complete.' });
    }
    if (draft.status === 'cancelled' && draft.scheduleStatus === 'on_site') {
        context.addIssue({ code: 'custom', path: ['scheduleStatus'], message: 'A cancelled subcontract cannot remain on site.' });
    }
}
export const procurementSubcontractDraftSchema = procurementSubcontractDraftBaseSchema.superRefine(validateSubcontractState);
export const procurementSubcontractCreateCommandSchema = z.strictObject({
    kind: z.literal('create'),
    commandId: z.string().uuid(),
    subcontractId: procurementOperationIdSchema,
    subcontract: procurementSubcontractDraftSchema,
    reason: procurementOperationReasonSchema,
});
export const procurementSubcontractReviseCommandSchema = z.strictObject({
    kind: z.literal('revise'),
    commandId: z.string().uuid(),
    expectedRevision: z.number().int().positive(),
    subcontract: procurementSubcontractDraftSchema,
    reason: procurementOperationReasonSchema,
});
export const procurementSubcontractCommandSchema = z.discriminatedUnion('kind', [
    procurementSubcontractReviseCommandSchema,
    procurementOperationStateCommandSchema,
]);
export const procurementSubcontractVersionSchema = z.strictObject({
    contractVersion: z.literal(procurementSubcontractsCapability),
    tenantId: z.string().uuid(),
    id: z.string().uuid(),
    subcontractId: z.string().uuid(),
    versionNumber: z.number().int().positive(),
    contentHash: z.string().regex(/^[a-f0-9]{64}$/),
    reason: procurementOperationReasonSchema,
    actorMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
    data: procurementSubcontractDraftBaseSchema.omit({ projectId: true }).extend({
        projectReference: projectsProjectOrderReferenceSnapshotSchema,
        projectAccessDecision: projectsProjectAccessDecisionSchema,
        estimatedCostCents: procurementOperationMoneyCentsSchema.nullable(),
        committedCostCents: procurementOperationMoneyCentsSchema.nullable(),
        actualCostCents: procurementOperationMoneyCentsSchema.nullable(),
    }).superRefine(validateSubcontractState),
});
export const procurementSubcontractSchema = z.strictObject({
    contractVersion: z.literal(procurementSubcontractsCapability),
    tenantId: z.string().uuid(),
    id: z.string().uuid(),
    revision: z.number().int().positive(),
    currentVersionId: z.string().uuid(),
    archivedAt: z.string().datetime({ offset: true }).nullable(),
    costsVisible: z.boolean(),
    createdByMembershipId: z.string().uuid(),
    updatedByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    currentVersion: procurementSubcontractVersionSchema,
});
export const procurementSubcontractListQuerySchema = z.strictObject({
    query: z.string().trim().max(160).default(''),
    projectId: z.string().uuid().nullable().default(null),
    status: procurementSubcontractStatusSchema.nullable().default(null),
    scheduleStatus: procurementSubcontractScheduleStatusSchema.nullable().default(null),
    includeArchived: z.boolean().default(false),
    limit: z.number().int().min(1).max(250).default(50),
    offset: z.number().int().nonnegative().default(0),
});
export const procurementSubcontractResponseSchema = z.strictObject({
    data: procurementSubcontractSchema,
    command: z.strictObject({ commandId: z.string().uuid(), idempotent: z.boolean() }).optional(),
});
export const procurementSubcontractsResponseSchema = z.strictObject({
    data: z.array(procurementSubcontractSchema),
    page: procurementOperationPageSchema,
});
//# sourceMappingURL=procurement-subcontract.contract.js.map